import React, { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { useUpdateProductMutation } from '@/apis/extended/productApi';
import { parseString } from '@/utils/helper';
import { Product } from '@/types/ProductType';

// Field constraints for validation and clamping
const CONSTRAINTS = {
  offerPrice: { min: 0, max: 100_000_000, step: 1 },
  hoaFees: { min: 0, max: 10_000, step: 1 },
  commission: { min: 0, max: 100, step: 0.5 },
  rent: { min: 0, max: 500_000, step: 1 },
  monthlyPropertyTaxes: { min: 0, max: 100_000, step: 1 },
  monthlyHomeownersInsurance: { min: 0, max: 50_000, step: 1 },
} as const;

const sharedInputSx: SxProps<Theme> = {
  '& .MuiInputLabel-root': { color: 'white' },
  '& .MuiInputBase-input': { color: 'white' },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.7)' },
};

interface ProductDetailPropertySpecificsModalProps {
  product: Product;
  open: boolean;
  onClose: () => void;
}

export const ProductDetailPropertySpecificsModal = ({
  product,
  open,
  onClose,
}: ProductDetailPropertySpecificsModalProps) => {
  const theme = useTheme();
  const owner = product.user;

  const [updateProduct, { isLoading: isUpdatingProduct }] =
    useUpdateProductMutation();

  const [form, setForm] = useState({
    offerPrice: '',
    hoaFees: '',
    commission: 0,
    rent: '',
    propertyType: 'single_family', // single_family, multi_unit, investment
    monthlyPropertyTaxes: '',
    monthlyHomeownersInsurance: '',
  });

  useEffect(() => {
    setForm({
      offerPrice: parseString(product?.loanInfo?.offerPrice),
      hoaFees: parseString(product?.loanInfo?.hoaFees),
      commission: product?.loanInfo?.commission || 0,
      rent: parseString(product?.loanInfo?.rent),
      propertyType: product?.loanInfo?.propertyType || 'single_family',
      monthlyPropertyTaxes: parseString(
        product?.loanInfo?.monthlyPropertyTaxes
      ),
      monthlyHomeownersInsurance: parseString(
        product?.loanInfo?.monthlyHomeownersInsurance
      ),
    });
  }, [product]);

  const isUpdated = React.useMemo(() => {
    return (
      form.offerPrice !== product?.loanInfo?.offerPrice?.toString() ||
      form.hoaFees !== product?.loanInfo?.hoaFees?.toString() ||
      form.commission !== product?.loanInfo?.commission ||
      form.rent !== product?.loanInfo?.rent?.toString() ||
      form.propertyType !==
        (product?.loanInfo?.propertyType || 'single_family') ||
      form.monthlyPropertyTaxes !==
        product?.loanInfo?.monthlyPropertyTaxes?.toString() ||
      form.monthlyHomeownersInsurance !==
        product?.loanInfo?.monthlyHomeownersInsurance?.toString()
    );
  }, [form, product]);

  // Allow intermediate typing (e.g. "1500." or "1") for numeric string fields; no clamp on keystroke
  const isValidNumericInput = (v: string) =>
    v === '' || v === '.' || /^\d*\.?\d*$/.test(v);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'commission') {
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
      const v =
        value === '' ? 0 : Math.min(100, Math.max(0, Number(value) || 0));
      setForm({ ...form, commission: v });
      return;
    }

    const key = name as keyof typeof CONSTRAINTS;
    if (key in CONSTRAINTS) {
      if (!isValidNumericInput(value)) return;
      setForm({ ...form, [name]: value });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const handleSelectChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const clampForSave = (
    key: keyof typeof CONSTRAINTS,
    raw: string | number
  ): number | undefined => {
    const n = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (raw === '' || raw === undefined || !Number.isFinite(n))
      return undefined;
    const { min, max } = CONSTRAINTS[key];
    const clamped = Math.min(max, Math.max(min, n));
    return clamped;
  };

  const handleSave = () => {
    updateProduct({
      id: product._id,
      offerPrice: clampForSave('offerPrice', form.offerPrice),
      hoaFees: clampForSave('hoaFees', form.hoaFees),
      commission: clampForSave('commission', form.commission),
      rent: clampForSave('rent', form.rent),
      propertyType: form.propertyType,
      monthlyPropertyTaxes: clampForSave(
        'monthlyPropertyTaxes',
        form.monthlyPropertyTaxes
      ),
      monthlyHomeownersInsurance: clampForSave(
        'monthlyHomeownersInsurance',
        form.monthlyHomeownersInsurance
      ),
    });
  };

  const isDscrProgram = owner?.loanProgram === USER_LOAN_PROGRAM.DSCR;
  const showRentalIncome =
    isDscrProgram ||
    form.propertyType === 'investment' ||
    form.propertyType === 'multi_unit';

  const rentalIncomeLabel = useMemo(() => {
    if (isDscrProgram) return 'Monthly rental income (DSCR)';
    if (form.propertyType === 'multi_unit') return 'Monthly rent (all units)';
    return 'Projected monthly rental income';
  }, [isDscrProgram, form.propertyType]);

  const rentalIncomeHelper = useMemo(() => {
    if (isDscrProgram)
      return 'Used for DSCR ratio. Enter projected or actual monthly rent.';
    if (form.propertyType === 'multi_unit')
      return 'Total monthly rent from all units. Max $500,000.';
    return 'Projected monthly rental income. Max $500,000.';
  }, [isDscrProgram, form.propertyType]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: theme.palette.primary.main,
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          borderRadius: 3,
          boxShadow: theme.shadows[24],
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.common.white,
            0.1
          )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ mr: 2, color: 'white' }} />
          <Typography variant="h6" fontWeight={700} color="white">
            Property Specifics
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Alert
          severity="info"
          sx={{
            mb: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.common.white,
              0.1
            )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
            '& .MuiAlert-message': { color: 'white' },
          }}
        >
          <Typography variant="body2" color="white">
            Adjust property-specific details including taxes, insurance, and
            fees that affect your loan calculations and monthly payments.
            {isDscrProgram && (
              <>
                {' '}
                Enter <strong>monthly rental income</strong> below to drive your
                DSCR ratio and loan terms.
              </>
            )}
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Property Type */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'white' }}>Property Type</InputLabel>
              <Select
                name="propertyType"
                value={form.propertyType}
                label="Property Type"
                onChange={handleSelectChange}
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                }}
              >
                <MenuItem value="single_family">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <i className="fa-light fa-home" />
                    Single Family Home
                  </Box>
                </MenuItem>
                <MenuItem value="multi_unit">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <i className="fa-light fa-building" />
                    Multi-Unit Property
                  </Box>
                </MenuItem>
                <MenuItem value="investment">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <i className="fa-light fa-chart-line" />
                    Investment Property
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Offer Price */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Offer Price"
              name="offerPrice"
              type="number"
              value={form.offerPrice}
              onChange={handleInputChange}
              inputProps={{
                min: CONSTRAINTS.offerPrice.min,
                max: CONSTRAINTS.offerPrice.max,
                step: CONSTRAINTS.offerPrice.step,
                inputMode: 'decimal',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText="Offer amount replaces list price in estimates"
              sx={sharedInputSx}
            />
          </Grid>

          {/* HOA Fees */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Monthly HOA Fees"
              name="hoaFees"
              type="number"
              value={form.hoaFees}
              onChange={handleInputChange}
              inputProps={{
                min: CONSTRAINTS.hoaFees.min,
                max: CONSTRAINTS.hoaFees.max,
                step: CONSTRAINTS.hoaFees.step,
                inputMode: 'decimal',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={`$0 – $${CONSTRAINTS.hoaFees.max.toLocaleString()} per month`}
              sx={sharedInputSx}
            />
          </Grid>

          {/* Buyers Agent Commission */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Buyers Agent Commission"
              name="commission"
              type="number"
              value={form.commission}
              onChange={handleInputChange}
              inputProps={{
                min: CONSTRAINTS.commission.min,
                max: CONSTRAINTS.commission.max,
                step: CONSTRAINTS.commission.step,
                inputMode: 'decimal',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      %
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={`${CONSTRAINTS.commission.min}–${CONSTRAINTS.commission.max}%`}
              sx={sharedInputSx}
            />
          </Grid>

          {/* Monthly Property Taxes */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Monthly Property Taxes"
              name="monthlyPropertyTaxes"
              type="number"
              value={form.monthlyPropertyTaxes}
              onChange={handleInputChange}
              inputProps={{
                min: CONSTRAINTS.monthlyPropertyTaxes.min,
                max: CONSTRAINTS.monthlyPropertyTaxes.max,
                step: CONSTRAINTS.monthlyPropertyTaxes.step,
                inputMode: 'decimal',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={`$0 – $${(
                CONSTRAINTS.monthlyPropertyTaxes.max / 1000
              ).toFixed(0)}k per month`}
              sx={sharedInputSx}
            />
          </Grid>

          {/* Monthly Homeowners Insurance */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Monthly Homeowners Insurance"
              name="monthlyHomeownersInsurance"
              type="number"
              value={form.monthlyHomeownersInsurance}
              onChange={handleInputChange}
              inputProps={{
                min: CONSTRAINTS.monthlyHomeownersInsurance.min,
                max: CONSTRAINTS.monthlyHomeownersInsurance.max,
                step: CONSTRAINTS.monthlyHomeownersInsurance.step,
                inputMode: 'decimal',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={`$0 – $${(
                CONSTRAINTS.monthlyHomeownersInsurance.max / 1000
              ).toFixed(0)}k per month`}
              sx={sharedInputSx}
            />
          </Grid>

          {/* Monthly Rental Income – DSCR calculation + Investment / Multi-Unit */}
          {showRentalIncome && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={rentalIncomeLabel}
                name="rent"
                type="number"
                value={form.rent}
                onChange={handleInputChange}
                inputProps={{
                  min: CONSTRAINTS.rent.min,
                  max: CONSTRAINTS.rent.max,
                  step: CONSTRAINTS.rent.step,
                  inputMode: 'decimal',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        $
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                helperText={rentalIncomeHelper}
                sx={sharedInputSx}
              />
            </Grid>
          )}

          {/* Current Values Display */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
            <Typography
              variant="subtitle2"
              fontWeight={600}
              gutterBottom
              color="white"
            >
              Current Property Details
            </Typography>
            <Box
              sx={{
                p: 2,
                background: `rgba(255, 255, 255, 0.05)`,
                borderRadius: 2,
                border: `1px solid rgba(255, 255, 255, 0.2)`,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    color="white"
                    sx={{ opacity: 0.8 }}
                  >
                    Offer Price
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="white">
                    ${product?.loanInfo?.offerPrice?.toLocaleString() || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    color="white"
                    sx={{ opacity: 0.8 }}
                  >
                    List Price
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="white">
                    ${product?.price?.toLocaleString() || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    color="white"
                    sx={{ opacity: 0.8 }}
                  >
                    Property Type
                  </Typography>
                  <Chip
                    label={form.propertyType
                      .replace('_', ' ')
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                    size="small"
                    sx={{
                      bgcolor: `rgba(255, 255, 255, 0.2)`,
                      color: 'white',
                      border: `1px solid rgba(255, 255, 255, 0.3)`,
                    }}
                  />
                </Grid>
                {product?.loanInfo?.zipCode && (
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      color="white"
                      sx={{ opacity: 0.8 }}
                    >
                      Location (Zip Code)
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="white">
                      {product.loanInfo.zipCode}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          background: theme.palette.primary.main,
          borderTop: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={isUpdatingProduct}
          disabled={!isUpdated}
          sx={{
            background: 'white',
            color: theme.palette.primary.main,
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.9)',
            },
          }}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
